import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { loginWithPassword, type LoginResult, type AuthTokens } from "./cognito";
import { saveTokens, loadTokens, clearTokens } from "./tokenStore";
import { apiFetch, setOnSessionExpired } from "../api/client";
import type { PortalRole } from "../components/Layout";

// Shape matches the GET /portal/me response verbatim (fn_portal_me returns
// snake_case client_id/client_name/role); apiFetch returns the raw body with
// no case transform, so these MUST stay snake_case or every customer's name
// silently reads as undefined.
export interface PortalUser {
  user_id: string;
  client_id: string | null;
  client_name: string | null;
  role: PortalRole;
}

interface AuthContextValue {
  user: PortalUser | null;
  initializing: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  completeLogin: (tokens: AuthTokens, email: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PortalUser | null>(null);
  const [initializing, setInitializing] = useState(true);

  const logout = () => {
    clearTokens();
    setUser(null);
  };

  useEffect(() => {
    setOnSessionExpired(logout);
    const tokens = loadTokens();
    if (!tokens) {
      setInitializing(false);
      return;
    }
    apiFetch<PortalUser>("/portal/me")
      .then(setUser)
      .catch(() => clearTokens())
      .finally(() => setInitializing(false));
  }, []);

  const completeLogin = async (tokens: AuthTokens, email: string) => {
    saveTokens({ ...tokens, email });
    const me = await apiFetch<PortalUser>("/portal/me");
    setUser(me);
  };

  return (
    <AuthContext.Provider value={{ user, initializing, login: loginWithPassword, completeLogin, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
